import {
  LanguageValues,
  OfferedInstrumentVersionValues,
  VocabularyPackValues,
} from "./catalogue.js";

/**
 * What a setting is for.
 *
 * Read by the administration page, which groups by it. It is here because a
 * configuration namespace with no distinction between "what this body has been
 * given" and "which version of a thing it works in" becomes a drawer, and nothing
 * can be said about the set as a whole.
 */
export type SettingKind = "entitlement" | "catalogue";

/**
 * What a lower layer of the chain may do to a value a higher one set.
 *
 * - `free` — the lower layer replaces it.
 * - `narrowing` — the lower layer may only reduce it: turn a capability off, or
 *   choose fewer of a set. It can never widen what it was given.
 *
 * Narrowing is enforced twice, and both are load-bearing. A write proposing a
 * value wider than the layer above allows is **refused**, so an administrator is
 * told rather than surprised. Resolution **clamps** as well, because a value that
 * was legal when written stops being legal the moment a layer above tightens —
 * and the alternative is that a body keeps a capability its programme has since
 * withdrawn, which is the one direction that must not fail open.
 *
 * There is deliberately no `sealed` — a value a lower layer may not touch at all.
 * Every setting is written by Thalamos today, so there is no layer to seal
 * against, and the branch would be unreachable. It becomes real when a setting is
 * mandated nationally, or when a body writes its own.
 */
export type OverridePolicy = "free" | "narrowing";

/**
 * Which layers of the chain may state a setting.
 *
 * - `chain` — any layer: a scope for every body beneath it, a body for itself.
 * - `organisation` — each body for itself alone. For a setting that decides what
 *   a body's people may read, because a scope confers configuration and never
 *   reach (`2026-09-11-a-configuration-scope-confers-no-reach`). A scope's
 *   statement of one is refused when written and ignored when resolved.
 */
export type SettingStatedBy = "chain" | "organisation";

/**
 * Where an administrator changes a setting.
 *
 * - `configuration_page` — on the configuration pages, at whichever layers may
 *   state it.
 * - `organisation_page` — on the organisation's own page and nowhere else. For
 *   a setting that decides who reads what about a child, so one route, and one
 *   kind of audit entry, records every change to it.
 */
export type SettingChangedOn = "configuration_page" | "organisation_page";

/**
 * A setting whose value is on or off.
 *
 * Narrowing means a lower layer may turn it off and not on, which is conjunction:
 * the resolved value is true only where every layer that spoke said true.
 */
const booleanSetting = <
  const Spec extends {
    kind: SettingKind;
    override: OverridePolicy;
    default: boolean;
    statedBy: SettingStatedBy;
    changedOn: SettingChangedOn;
  },
>(
  spec: Spec,
) => ({ valueKind: "boolean" as const, ...spec });

/**
 * A setting whose value is one member of a closed set.
 *
 * Always `free`: "narrower" means nothing for a single choice, so a policy field
 * here would be a value the fold could not act on. Where one choice has to stay
 * within what another setting allows — a default language within the offered ones
 * — that is an invariant over the resolved configuration, stated below, not an
 * override policy.
 */
const choiceSetting = <
  const Choices extends readonly [string, ...string[]],
>(spec: {
  kind: SettingKind;
  choices: Choices;
  default: Choices[number];
}) => ({
  valueKind: "choice" as const,
  override: "free" as const,
  statedBy: "chain" as const,
  changedOn: "configuration_page" as const,
  ...spec,
});

/**
 * A setting whose value is several members of a closed set.
 *
 * Narrowing is subset, and the fold intersects rather than replaces. Folding with
 * object spread would let a lower layer replace the set wholesale, including with
 * a wider one, silently.
 */
const choiceSetSetting = <
  const Choices extends readonly [string, ...string[]],
>(spec: {
  kind: SettingKind;
  override: OverridePolicy;
  choices: Choices;
  default: readonly Choices[number][];
}) => ({
  valueKind: "choice_set" as const,
  statedBy: "chain" as const,
  changedOn: "configuration_page" as const,
  ...spec,
});

/**
 * A setting whose value is a whole number within stated bounds.
 *
 * Always `free`, as a single choice is: whether a larger number is the narrower
 * one depends on what it counts, so a lower layer replaces it outright.
 */
const wholeNumberSetting = (spec: {
  kind: SettingKind;
  minimum: number;
  maximum: number;
  default: number;
}) => ({
  valueKind: "whole_number" as const,
  override: "free" as const,
  statedBy: "chain" as const,
  changedOn: "configuration_page" as const,
  ...spec,
});

/**
 * Every setting Haven has, declared once.
 *
 * A key's type, its default, what a lower layer may do to it, and the set it
 * draws from are all here — so adding one is an entry here and nothing else. The
 * administration page renders from these declarations rather than from a
 * hand-written control per key, which is what stops this growing an editor
 * alongside it.
 *
 * A key is stored by name, so renaming one abandons every stored value under the
 * old name. Add and retire; never rename.
 */
export const settingDeclarations = {
  /**
   * Whether two records may be declared to be one young person.
   *
   * Defaults on: a body that has said nothing about merging is not a body that
   * has been refused it.
   */
  recordMergeEnabled: booleanSetting({
    kind: "entitlement",
    override: "narrowing",
    default: true,
    statedBy: "chain",
    changedOn: "configuration_page",
  }),

  /**
   * Whether this body's people read what a finished assessment says about a
   * young person the body reaches only through one of its members.
   *
   * Off until Thalamos turns it on for the body: reach carried up through
   * membership shows the young person and where each assessment stands, and
   * reading what they say is a further grant made body by body.
   */
  readsApprovedAssessmentsThroughMembers: booleanSetting({
    kind: "entitlement",
    override: "free",
    default: false,
    statedBy: "organisation",
    changedOn: "organisation_page",
  }),

  /**
   * Which versions of an assessment instrument may be started.
   *
   * Narrowing: a body may use fewer than the programme above it allows, never
   * more. What it decides is what may be *started* — an assessment already under
   * way keeps the version it began on, so removing one here never changes what a
   * finished assessment means.
   */
  availableInstrumentVersions: choiceSetSetting({
    kind: "catalogue",
    override: "narrowing",
    choices: OfferedInstrumentVersionValues,
    // Everything published: the setting narrows downwards, so the default is
    // the ceiling a programme reduces rather than a starting selection.
    default: OfferedInstrumentVersionValues,
  }),

  /**
   * Which languages this body offers.
   *
   * Policy rather than preference: offering a language means someone has
   * commissioned and maintains the translations for it. Which of them a person
   * reads in is theirs, chosen from this set.
   */
  availableLanguages: choiceSetSetting({
    kind: "catalogue",
    override: "narrowing",
    choices: LanguageValues,
    default: ["en"],
  }),

  /**
   * How many days after an assessment was approved or completed it may still
   * give a reassessment its ratings, counted to the day the new one starts.
   *
   * An older assessment is still linked as the one the new one follows; it only
   * stops being offered for carry-over.
   */
  reassessmentCarryOverDays: wholeNumberSetting({
    kind: "entitlement",
    minimum: 0,
    // Ten years: far past any reassessment cycle, and a bound so a slip of the
    // keyboard cannot make every assessment ever finished a source of ratings.
    maximum: 3650,
    default: 365,
  }),

  /** The language someone gets before they have chosen one. */
  defaultLanguage: choiceSetting({
    kind: "catalogue",
    choices: LanguageValues,
    default: "en",
  }),

  /**
   * What the product calls the subject of a record.
   *
   * The body's, not the reader's: it is the register a body writes in rather than
   * something a person prefers, which is why there is no personal counterpart to
   * it as there is for language.
   */
  vocabularyPack: choiceSetting({
    kind: "catalogue",
    choices: VocabularyPackValues,
    default: "young_person",
  }),
} as const;

export type SettingDeclarations = typeof settingDeclarations;

/** The name of a setting. */
export type SettingKey = keyof SettingDeclarations;

export const settingKeys = Object.keys(settingDeclarations) as SettingKey[];

/**
 * The type a declaration's value has.
 *
 * Derived from the declaration rather than stated beside it, so a setting's
 * choices and its type cannot disagree — widening the catalogue widens the type
 * of every setting drawing on it, and a stored value outside it stops compiling
 * at the place that produces it.
 */
type ValueOfDeclaration<Declaration> = Declaration extends {
  valueKind: "boolean";
}
  ? boolean
  : Declaration extends { valueKind: "whole_number" }
    ? number
    : Declaration extends {
          valueKind: "choice";
          choices: readonly (infer Choice)[];
        }
      ? Choice
      : Declaration extends {
            valueKind: "choice_set";
            choices: readonly (infer Choice)[];
          }
        ? Choice[]
        : never;

export type SettingValue<Key extends SettingKey> = ValueOfDeclaration<
  SettingDeclarations[Key]
>;

/** Every setting, with a value for each. What a resolved chain produces. */
export type Configuration = {
  [Key in SettingKey]: SettingValue<Key>;
};

/** What one layer of the chain says. A layer speaks about the keys it sets and no others. */
export type PartialConfiguration = Partial<Configuration>;

/**
 * What every body gets before anything is set about it.
 *
 * In code rather than in a row, so the bottom of every chain is reviewed,
 * version-controlled and impossible to edit into a bad state in a running
 * environment. Written out per key rather than derived from the declarations,
 * because `Configuration` then checks both halves: a new setting that is not
 * given a default here does not compile.
 */
export const defaultConfiguration: Configuration = {
  recordMergeEnabled: settingDeclarations.recordMergeEnabled.default,
  readsApprovedAssessmentsThroughMembers:
    settingDeclarations.readsApprovedAssessmentsThroughMembers.default,
  availableInstrumentVersions: [
    ...settingDeclarations.availableInstrumentVersions.default,
  ],
  reassessmentCarryOverDays:
    settingDeclarations.reassessmentCarryOverDays.default,
  availableLanguages: [...settingDeclarations.availableLanguages.default],
  defaultLanguage: settingDeclarations.defaultLanguage.default,
  vocabularyPack: settingDeclarations.vocabularyPack.default,
};

/** A setting each body states for itself alone, which no scope may state. */
export type OrganisationSettingKey = {
  [
    Key in SettingKey
  ]: SettingDeclarations[Key]["statedBy"] extends "organisation" ? Key : never;
}[SettingKey];

/** Whether a layer of the chain may state a setting. */
export const mayStateSetting = (
  key: SettingKey,
  holder: { kind: "default" | "scope" | "organisation" },
): boolean =>
  settingDeclarations[key].statedBy === "chain" || holder.kind !== "scope";

/** Whether the configuration pages show and change a setting. */
export const isChangedOnTheConfigurationPage = (key: SettingKey): boolean =>
  settingDeclarations[key].changedOn === "configuration_page";

/** Whether a string names a setting. Narrows, so an unknown key cannot be stored or read. */
export const isSettingKey = (key: string): key is SettingKey =>
  Object.hasOwn(settingDeclarations, key);
